import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Alert,
  AlertIcon,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Text,
  Spinner,
  Box,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Textarea,
  Wrap,
  WrapItem,
  Badge,
} from '@chakra-ui/react'
import { Ban, Check, CheckCircle, Play, Plus, RotateCcw, Save, SkipForward, UserRoundCog, X } from 'lucide-react'
import axios from 'axios'
import { TicketService } from '@/lib/services/ticket'
import {
  childTicketService,
  type ChildTicketPayload,
  type ChildTicketTask,
  type TaskCompletionPreflight,
} from '@/lib/services/childTicketService'
import { taskService, type TaskChecklistProgress } from '@/lib/services/taskService'
import { taskTemplateService } from '@/lib/services/taskTemplateService'
import type { AgentOption } from '@/types/ticket'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'
import SlaStatusStrip from '@/components/helpdesk/TicketDetail/overview/SlaStatusStrip'
import TaskFormRenderer from './TaskFormRenderer'
import TaskStatusPill from './TaskStatusPill'
import TaskPriorityBadge from './TaskPriorityBadge'
import { resolveTaskUiType, TaskTypeBadge } from './taskTypeBadge'
import TaskAssigneePicker from './TaskAssigneePicker'
import TaskWatchersControl, { type TaskWatcherEntry } from './TaskWatchersControl'
import TaskAttachmentsList from './TaskAttachmentsList'
import TaskTimeline from './TaskTimeline'
import FeVisitTimeline from './FeVisitTimeline'
import TaskChecklist from './TaskChecklist'
import TaskDependencies from './TaskDependencies'
import FeExpenseReviewPanel from './FeExpenseReviewPanel'
import ReassignEngineerModal from './ReassignEngineerModal'
import PartProcurementPanel from './PartProcurementPanel'
import { canCancelFeVisit, canForceCloseFeVisit } from './feVisitLifecycle'
import { isTemplateBackedCreate, matchesTaskSourceField, templateCodeForSourceField } from './taskSourceFieldUtils'
import { taskHasAssigneeField, taskHostsDocumentCollection, taskHostsFeVisitTracking } from './modules/resolveFormFields'
import { validateTaskFormFields } from './modules/validateTaskForm'
import { getPartsProcurementCompletion, isFeVisitSuperseded, isTaskComplete } from './taskUtils'
import type { FESchedule, TicketPart } from '@/types/parentTicket'

const DOCUMENT_PURPOSE_OPTIONS = [
  { value: 'collection', label: 'Collection — gather documents from customer' },
  { value: 'validation', label: 'Validation — verify submitted documents' },
  { value: 'approval', label: 'Approval — approve or reject documents' },
] as const

type StatusOption = {
  label: string
  value: string
  scope?: string | null
  is_terminal?: boolean
  color?: string | null
  sort_order?: number | null
}
const STATUS_CATALOG_STORAGE_KEY = 'helpdesk.child_ticket_status_catalog'

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

function toDatetimeLocalValue(iso?: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function fromDatetimeLocalValue(value: string): string | null {
  if (!value.trim()) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

type Props = {
  isOpen: boolean
  onClose: () => void
  parentTicketId: string
  task?: ChildTicketTask | null
  siblingTasks?: ChildTicketTask[]
  onOpenTask?: (task: ChildTicketTask) => void
  onSaved: () => void
  ticketPropertiesProps?: Record<string, unknown>
  createDefaults?: Partial<ChildTicketTask> | null
}

export default function TaskDetailDrawer({
  isOpen,
  onClose,
  parentTicketId,
  task,
  siblingTasks = [],
  onOpenTask,
  onSaved,
  ticketPropertiesProps,
  createDefaults,
}: Props) {
  const { showSuccess, showError } = useToastNotification()
  const isEdit = Boolean(task?.id)
  const [loadedTask, setLoadedTask] = useState<ChildTicketTask | null>(null)
  const [loadingTask, setLoadingTask] = useState(false)
  const [title, setTitle] = useState('')
  const [status, setStatus] = useState('pending')
  const [priority, setPriority] = useState('medium')
  const [dueAt, setDueAt] = useState('')
  const [assignedTo, setAssignedTo] = useState<number | ''>('')
  const [assigneeSearch, setAssigneeSearch] = useState('')
  const [watchers, setWatchers] = useState<TaskWatcherEntry[]>([])
  const [assigneeResults, setAssigneeResults] = useState<AgentOption[]>([])
  const [assigneeLoading, setAssigneeLoading] = useState(false)
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>([])
  const [saving, setSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState<
    'start' | 'complete' | 'skip' | 'cancel' | 'reopen' | 'reassign' | 'cancel-visit' | 'force-close' | null
  >(null)
  const [skipReason, setSkipReason] = useState('')
  const [skipPopoverOpen, setSkipPopoverOpen] = useState(false)
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [tabIndex, setTabIndex] = useState(0)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [cancelReason, setCancelReason] = useState('')
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false)
  const [reopenReason, setReopenReason] = useState('')
  const [reassignModalOpen, setReassignModalOpen] = useState(false)
  const [linkedFeSchedule, setLinkedFeSchedule] = useState<FESchedule | null>(null)
  const [linkedFeScheduleLoading, setLinkedFeScheduleLoading] = useState(false)
  const [cancelVisitDialogOpen, setCancelVisitDialogOpen] = useState(false)
  const [cancelVisitReason, setCancelVisitReason] = useState('')
  const [forceCloseDialogOpen, setForceCloseDialogOpen] = useState(false)
  const [forceCloseReason, setForceCloseReason] = useState('')
  const [procurementParts, setProcurementParts] = useState<TicketPart[]>([])
  const [loadingDefaults, setLoadingDefaults] = useState(false)
  const [completionPreflight, setCompletionPreflight] = useState<TaskCompletionPreflight | null>(null)
  const [checklistProgress, setChecklistProgress] = useState<TaskChecklistProgress>({
    total: 0,
    checked: 0,
    required_total: 0,
    required_checked: 0,
  })
  const cancelLeastDestructiveRef = useRef<HTMLButtonElement>(null)
  const reopenLeastDestructiveRef = useRef<HTMLButtonElement>(null)
  const cancelVisitLeastDestructiveRef = useRef<HTMLButtonElement>(null)
  const forceCloseLeastDestructiveRef = useRef<HTMLButtonElement>(null)

  const activeTask = loadedTask ?? task ?? null
  const isDocCollectionCreate = !isEdit && isTemplateBackedCreate(createDefaults)
  const documentPurpose =
    typeof formData.purpose === 'string' && formData.purpose.trim()
      ? formData.purpose.trim()
      : 'collection'

  useEffect(() => {
    if (!isOpen || !activeTask?.id) {
      setCompletionPreflight(null)
      return
    }

    let cancelled = false
    void childTicketService.completionPreflight(parentTicketId, activeTask.id)
      .then((result) => {
        if (!cancelled) setCompletionPreflight(result)
      })
      .catch(() => {
        if (!cancelled) setCompletionPreflight(null)
      })

    return () => { cancelled = true }
  }, [isOpen, parentTicketId, activeTask?.id])

  const resetFromTask = useCallback((source: ChildTicketTask | Partial<ChildTicketTask> | null | undefined) => {
    setTitle(source?.title ?? source?.notes ?? '')
    setStatus(source?.status ?? 'pending')
    setPriority(source?.priority ?? 'medium')
    setDueAt(toDatetimeLocalValue(source?.due_at))
    setAssignedTo(source?.assigned_to ?? '')
    setAssigneeSearch(source?.assignee_name ?? '')
    setWatchers(
      Array.isArray(source?.watchers)
        ? source.watchers.map((w) => ({
            user_id: Number(w.user_id),
            name: w.name || `User #${w.user_id}`,
          }))
        : []
    )
    setAssigneeResults([])
    setFormData((source?.form_data as Record<string, unknown>) ?? {})
    setSkipReason('')
    setSkipPopoverOpen(false)
    setTabIndex(0)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    if (task?.id) {
      resetFromTask(task)
      setLoadedTask(null)
      return
    }

    resetFromTask({
      title: createDefaults?.title ?? '',
      status: createDefaults?.status ?? 'pending',
      priority: createDefaults?.priority ?? 'medium',
      ...createDefaults,
      form_data: {
        purpose: 'collection',
        ...((createDefaults?.form_data as Record<string, unknown> | undefined) ?? {}),
      },
    })
    setLoadedTask(null)
  }, [isOpen, task, createDefaults, resetFromTask])

  useEffect(() => {
    if (!isOpen || task?.id || !isTemplateBackedCreate(createDefaults)) return

    const templateCode =
      templateCodeForSourceField(createDefaults?.source_field) ??
      ((createDefaults?.action_type ?? '').trim() === 'parts_procurement'
        ? 'parts_procurement'
        : 'document_collection')

    let cancelled = false
    setLoadingDefaults(true)
    const purpose =
      typeof createDefaults?.form_data === 'object' &&
      createDefaults.form_data &&
      typeof (createDefaults.form_data as Record<string, unknown>).purpose === 'string'
        ? String((createDefaults.form_data as Record<string, unknown>).purpose)
        : 'collection'

    const context =
      templateCode === 'document_collection'
        ? { document_purpose: purpose, source_field: 'attachments' }
        : { source_field: 'tools_parts' }

    taskTemplateService
      .getDefaults(templateCode, parentTicketId, context)
      .then((defaults) => {
        if (cancelled) return
        setTitle(
          defaults.title ||
            createDefaults?.title ||
            (templateCode === 'parts_procurement' ? 'Parts procurement' : 'Document collection'),
        )
        setStatus(defaults.status || 'pending')
        setPriority(defaults.priority || 'medium')
        setDueAt(toDatetimeLocalValue(defaults.due_at))
        setAssignedTo(defaults.assigned_to ?? '')
        setFormData({
          ...(defaults.form_data ?? {}),
          ...(templateCode === 'document_collection'
            ? { purpose: (defaults.form_data?.purpose as string | undefined) ?? purpose }
            : {}),
        })
      })
      .catch((err) => {
        if (!cancelled) showError(err as Error)
      })
      .finally(() => {
        if (!cancelled) setLoadingDefaults(false)
      })

    return () => {
      cancelled = true
    }
  }, [createDefaults, isOpen, parentTicketId, showError, task?.id])

  useEffect(() => {
    if (!isOpen || !task?.id) return
    let cancelled = false
    setLoadingTask(true)
    taskService
      .show(task.id)
      .then((fresh) => {
        if (cancelled) return
        setLoadedTask(fresh)
        resetFromTask(fresh)
      })
      .catch((err) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : String(err ?? '')
        const missing =
          /no longer exists|No query results for model .*ChildTicket/i.test(message) ||
          (axios.isAxiosError(err) && err.response?.status === 404)
        if (missing) {
          showError(
            new Error(
              'This task no longer exists (it may have been removed or the workflow was reset). Refreshing the list.',
            ),
          )
          void onSaved()
          onClose()
          return
        }
        setLoadedTask(task)
      })
      .finally(() => {
        if (!cancelled) setLoadingTask(false)
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, task, resetFromTask, onClose, onSaved, showError])

  useEffect(() => {
    if (!isOpen || !task?.id) {
      if (!isOpen) {
        setChecklistProgress({
          total: 0,
          checked: 0,
          required_total: 0,
          required_checked: 0,
        })
      }
      return
    }
    let cancelled = false
    taskService
      .listChecklist(task.id)
      .then((result) => {
        if (!cancelled) setChecklistProgress(result.progress)
      })
      .catch(() => {
        /* badge stays empty until Checklist tab loads */
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, task?.id])

  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    apiClient
      .get<{
        data: Array<{
          code: string
          label?: string | null
          scope?: string | null
          is_terminal?: boolean
          color?: string | null
          sort_order?: number | null
        }>
      }>('/service-catalog/child-ticket-statuses', { params: { per_page: 100 } })
      .then((res) => {
        if (cancelled) return
        const items = res.data?.data
        if (Array.isArray(items)) {
          const sorted = [...items].sort((a, b) => {
            const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0)
            if (orderDiff !== 0) return orderDiff
            return (a.label || a.code).localeCompare(b.label || b.code)
          })
          setStatusOptions(
            sorted.map((item) => ({
              label: item.label || item.code,
              value: item.code,
              scope: item.scope ?? null,
              is_terminal: item.is_terminal === true,
              color: item.color ?? null,
              sort_order: item.sort_order ?? 0,
            }))
          )
          window.localStorage.setItem(
            STATUS_CATALOG_STORAGE_KEY,
            JSON.stringify(
              sorted.map((item) => ({
                code: item.code,
                scope: item.scope ?? null,
                is_terminal: item.is_terminal === true,
                color: item.color ?? null,
                sort_order: item.sort_order ?? 0,
              }))
            )
          )
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [isOpen])

  const statusRecord = useMemo(
    () => statusOptions.find((option) => option.value === status) ?? null,
    [status, statusOptions]
  )

  const filteredStatusOptions = useMemo(() => {
    const currentScope = statusOptions.find((o) => o.value === status)?.scope ?? 'generic'
    return statusOptions.filter(
      (o) => o.value === status || o.scope === 'generic' || o.scope === currentScope
    )
  }, [status, statusOptions])

  const searchAssignees = useCallback(async (term: string) => {
    setAssigneeSearch(term)
    if (term.length < 2) {
      setAssigneeResults([])
      return
    }
    setAssigneeLoading(true)
    try {
      const res = await TicketService.searchHelpdeskAgentOptions(term)
      setAssigneeResults(res.success && res.data ? res.data : [])
    } finally {
      setAssigneeLoading(false)
    }
  }, [])

  const handleSave = async () => {
    if (!title.trim()) {
      showError(new Error('Title is required'))
      return
    }
    if (isEdit && activeTask) {
      const validation = validateTaskFormFields(activeTask, parentTicketId, formData, ticketPropertiesProps)
      if (!validation.valid) {
        showError(new Error(validation.message ?? 'Please complete required task fields'))
        return
      }
    }
    setSaving(true)
    try {
      if (isEdit && activeTask?.id) {
        await taskService.update(activeTask.id, {
          title: title.trim(),
          status,
          priority: priority || null,
          due_at: fromDatetimeLocalValue(dueAt),
          assigned_to: assignedTo === '' ? null : Number(assignedTo),
          form_data: formData,
        })
        showSuccess('Task updated')
      } else if (isTemplateBackedCreate(createDefaults)) {
        const templateCode =
          templateCodeForSourceField(createDefaults?.source_field) ??
          ((createDefaults?.action_type ?? '').trim() === 'parts_procurement'
            ? 'parts_procurement'
            : 'document_collection')
        const context =
          templateCode === 'document_collection'
            ? {
                document_purpose: documentPurpose,
                source_field: 'attachments' as const,
              }
            : {
                source_field: 'tools_parts' as const,
              }
        const created = await taskTemplateService.createTaskFromTemplate(parentTicketId, {
          template_code: templateCode,
          context,
        })
        const needsPatch =
          title.trim() !== (created.title ?? '') ||
          status !== (created.status ?? '') ||
          priority !== (created.priority ?? 'medium') ||
          (assignedTo === '' ? null : Number(assignedTo)) !== (created.assigned_to ?? null) ||
          JSON.stringify(formData) !== JSON.stringify(created.form_data ?? {})

        if (needsPatch && created.id) {
          await taskService.update(created.id, {
            title: title.trim(),
            status,
            priority: priority || null,
            due_at: fromDatetimeLocalValue(dueAt),
            assigned_to: assignedTo === '' ? null : Number(assignedTo),
            form_data: formData,
          })
        }
        showSuccess('Task created')
      } else {
        const payload: ChildTicketPayload = {
          title: title.trim(),
          status,
          assigned_to: assignedTo === '' ? null : Number(assignedTo),
          form_data: formData,
        }
        if (createDefaults?.action_type) payload.action_type = createDefaults.action_type
        if (createDefaults?.source_field) payload.source_field = createDefaults.source_field
        await childTicketService.create(parentTicketId, payload)
        showSuccess('Task created')
      }
      onSaved()
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setSaving(false)
    }
  }

  const handleStart = async () => {
    if (!activeTask?.id) return
    setActionLoading('start')
    try {
      await childTicketService.start(parentTicketId, activeTask.id)
      showSuccess('Task started')
      onSaved()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleComplete = async () => {
    if (!activeTask?.id) return
    const validation = validateTaskFormFields(activeTask, parentTicketId, formData, ticketPropertiesProps)
    if (!validation.valid) {
      showError(new Error(validation.message ?? 'Please complete required task fields before marking complete'))
      return
    }

    setActionLoading('complete')
    try {
      const preflight = await childTicketService.completionPreflight(parentTicketId, activeTask.id)
      if (!preflight.can_complete) {
        const details = preflight.blockers
          .map((blocker) => `${blocker.message}${blocker.items.length > 0 ? ` (${blocker.items.length})` : ''}`)
          .join(' ')
        showError(new Error(details || 'This task is not ready to complete'))
        return
      }

      const { items, progress } = await taskService.listChecklist(activeTask.id)
      setChecklistProgress(progress)
      if (progress.required_total > 0 && progress.required_checked < progress.required_total) {
        const missing = items
          .filter((item) => item.required && !item.checked)
          .map((item) => item.label)
          .filter(Boolean)
        showError(
          new Error(
            missing.length > 0
              ? `Cannot complete task: required checklist items are unchecked: ${missing.join(', ')}`
              : 'Cannot complete task: required checklist items are unchecked',
          ),
        )
        setTabIndex(isPartsProcurementUi ? 2 : 1)
        return
      }

      await childTicketService.complete(parentTicketId, activeTask.id, status)
      showSuccess('Task completed')
      onSaved()
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleSkip = async () => {
    if (!activeTask?.id) return
    setActionLoading('skip')
    try {
      await childTicketService.skip(parentTicketId, activeTask.id, skipReason.trim() || undefined)
      showSuccess('Task skipped')
      setSkipPopoverOpen(false)
      onSaved()
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleCancel = async () => {
    if (!activeTask?.id) return
    if (resolveTaskUiType(activeTask) === 'fe_visit') {
      showError(new Error('Use Cancel visit or Force close for field visits'))
      return
    }
    setActionLoading('cancel')
    try {
      await taskService.cancel(activeTask.id, cancelReason)
      showSuccess('Task cancelled')
      setCancelDialogOpen(false)
      setCancelReason('')
      onSaved()
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleReopen = async () => {
    if (!activeTask?.id) return
    if (resolveTaskUiType(activeTask) === 'fe_visit') {
      showError(new Error('Use Cancel visit or Force close for field visits'))
      return
    }
    setActionLoading('reopen')
    try {
      await taskService.reopen(activeTask.id, reopenReason || undefined)
      showSuccess('Task reopened')
      setReopenDialogOpen(false)
      setReopenReason('')
      onSaved()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleCancelVisit = async () => {
    if (!activeTask?.id) return
    setActionLoading('cancel-visit')
    try {
      await childTicketService.cancelFeVisit(
        parentTicketId,
        activeTask.id,
        cancelVisitReason.trim() || undefined,
      )
      showSuccess('Field visit cancelled')
      setCancelVisitDialogOpen(false)
      setCancelVisitReason('')
      onSaved()
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const handleForceCloseVisit = async () => {
    if (!activeTask?.id) return
    setActionLoading('force-close')
    try {
      await childTicketService.forceCloseFeVisit(
        parentTicketId,
        activeTask.id,
        forceCloseReason.trim() || undefined,
      )
      showSuccess('Field visit closed')
      setForceCloseDialogOpen(false)
      setForceCloseReason('')
      onSaved()
      onClose()
    } catch (err) {
      showError(err as Error)
    } finally {
      setActionLoading(null)
    }
  }

  const taskComplete = activeTask ? isTaskComplete(activeTask) : false
  const taskSkipped = Boolean(activeTask?.skipped)
  const taskSuperseded = Boolean(activeTask && isFeVisitSuperseded(activeTask))
  const taskReadOnly = taskComplete || taskSkipped || taskSuperseded
  const canStart = isEdit && activeTask && !taskComplete && !taskSkipped && !activeTask.actual_start && !taskSuperseded
  const isFeVisitUi = Boolean(activeTask && resolveTaskUiType(activeTask) === 'fe_visit')
  const isOnsiteCoordinationUi = activeTask?.action_type === 'onsite_coordination'
  const isPartsProcurementUi = Boolean(
    activeTask && resolveTaskUiType(activeTask) === 'parts_procurement',
  )
  const showCancelVisit = isFeVisitUi && !taskSuperseded && canCancelFeVisit(activeTask?.status)
  const showForceCloseVisit = isFeVisitUi && !taskSuperseded && canForceCloseFeVisit(activeTask?.status)
  const canReassignFeVisit =
    isFeVisitUi &&
    !taskComplete &&
    !taskSkipped &&
    !taskSuperseded &&
    Boolean(linkedFeSchedule?.schedule_date)
  const showOrphanFeVisitWarning =
    isFeVisitUi &&
    !taskComplete &&
    !taskSkipped &&
    !taskSuperseded &&
    !linkedFeScheduleLoading &&
    !linkedFeSchedule?.schedule_date
  const replacementTask = useMemo(() => {
    const replacementId = activeTask?.reassigned_to_task_id
    if (!replacementId) return null
    return siblingTasks.find((sibling) => sibling.id === replacementId) ?? null
  }, [activeTask?.reassigned_to_task_id, siblingTasks])
  const moduleRendersBaseFields = ['parts_procurement', 'document_collection'].includes(
    (activeTask?.action_type ?? '').trim(),
  )
  const showAssignee =
    !taskSuperseded &&
    !moduleRendersBaseFields &&
    (!isEdit || !activeTask || !activeTask.form_fields?.length || taskHasAssigneeField(activeTask))
  const showFeVisitTab = Boolean(
    activeTask && matchesTaskSourceField(activeTask, 'fe_visit') && !taskHostsFeVisitTracking(activeTask),
  )
  const showExpenseTab = Boolean(activeTask && matchesTaskSourceField(activeTask, 'fe_visit'))
  const showAttachmentsTab = Boolean(activeTask && !taskHostsDocumentCollection(activeTask))

  useEffect(() => {
    if (!isOpen || !isFeVisitUi || !activeTask?.id) {
      setLinkedFeSchedule(null)
      setLinkedFeScheduleLoading(false)
      return
    }

    let cancelled = false
    setLinkedFeScheduleLoading(true)

    void TicketService.getFESchedules(parentTicketId)
      .then((res) => {
        if (cancelled) return
        if (!res.success) {
          setLinkedFeSchedule(null)
          return
        }
        const linked =
          (res.data ?? []).find((schedule) => Number(schedule.child_ticket_id) === activeTask.id) ?? null
        setLinkedFeSchedule(linked)
      })
      .catch(() => {
        if (!cancelled) setLinkedFeSchedule(null)
      })
      .finally(() => {
        if (!cancelled) setLinkedFeScheduleLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [activeTask?.id, isFeVisitUi, isOpen, parentTicketId])

  const ticketPartsFromProps = useMemo(() => {
    const ticket = ticketPropertiesProps?.ticket as { ticketParts?: TicketPart[] } | undefined
    return Array.isArray(ticket?.ticketParts) ? ticket.ticketParts : []
  }, [ticketPropertiesProps])

  const refreshProcurementParts = useCallback(async () => {
    const res = await TicketService.getTicketParts(parentTicketId)
    if (res.success && res.data) {
      setProcurementParts(res.data)
    }
  }, [parentTicketId])

  useEffect(() => {
    if (!isOpen || !isPartsProcurementUi || !activeTask?.id) {
      setProcurementParts([])
      return
    }

    if (ticketPartsFromProps.length > 0) {
      setProcurementParts(ticketPartsFromProps)
      return
    }

    let cancelled = false
    void TicketService.getTicketParts(parentTicketId).then((res) => {
      if (cancelled) return
      if (res.success && res.data) {
        setProcurementParts(res.data)
      }
    })

    return () => {
      cancelled = true
    }
  }, [isOpen, isPartsProcurementUi, activeTask?.id, parentTicketId, ticketPartsFromProps])

  const partsCompletion = useMemo(() => {
    if (!activeTask?.id) {
      return { partsLinked: false, partsComplete: true, unresolvedCount: 0, linkedCount: 0, linkedParts: [] }
    }
    return getPartsProcurementCompletion(activeTask.id, procurementParts)
  }, [procurementParts, activeTask?.id])

  const linkedProcurementParts = partsCompletion.linkedParts
  const partsLinked = isPartsProcurementUi && partsCompletion.partsLinked
  const unresolvedProcurementCount = partsCompletion.unresolvedCount
  const partsComplete = partsCompletion.partsComplete
  const blockCompleteForParts = isPartsProcurementUi && partsLinked && !partsComplete
  const skipRequiresReasonForParts = isPartsProcurementUi && partsLinked

  const checklistBadgeLabel =
    checklistProgress.required_total > 0
      ? `${checklistProgress.required_checked}/${checklistProgress.required_total}`
      : checklistProgress.total > 0
        ? `${checklistProgress.checked}/${checklistProgress.total}`
        : null
  const checklistIncomplete =
    checklistProgress.required_total > 0 &&
    checklistProgress.required_checked < checklistProgress.required_total

  const handleChecklistProgressChange = useCallback((progress: TaskChecklistProgress) => {
    setChecklistProgress(progress)
  }, [])

  const mergedTicketPropertiesProps = useMemo(
    () => ({
      ...ticketPropertiesProps,
      taskMetaHandlers: {
        priority,
        dueAt,
        assignedTo,
        assigneeName: assigneeSearch,
        onChange: (patch: {
          priority?: string
          dueAt?: string
          assignedTo?: number | ''
          assigneeName?: string
        }) => {
          if (patch.priority !== undefined) setPriority(patch.priority)
          if (patch.dueAt !== undefined) setDueAt(toDatetimeLocalValue(patch.dueAt || null))
          if (patch.assignedTo !== undefined) setAssignedTo(patch.assignedTo)
          if (patch.assigneeName !== undefined) setAssigneeSearch(patch.assigneeName)
        },
        isReadOnly: taskComplete || taskSkipped || taskSuperseded,
      },
    }),
    [
      assigneeSearch,
      assignedTo,
      dueAt,
      priority,
      taskComplete,
      taskSkipped,
      ticketPropertiesProps,
    ],
  )

  const renderCreateBody = () => (
    <VStack align="stretch" spacing={4}>
      {loadingDefaults ? (
        <HStack py={2}>
          <Spinner size="sm" />
          <Text fontSize="sm">Resolving template defaults…</Text>
        </HStack>
      ) : null}
      <FormControl isRequired>
        <FormLabel>Title</FormLabel>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task description" />
      </FormControl>
      <HStack align="flex-start" spacing={3}>
        <FormControl flex="1">
          <FormLabel>Status</FormLabel>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {filteredStatusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormControl>
      </HStack>
      {isDocCollectionCreate ? (
        <FormControl isRequired>
          <FormLabel>Purpose</FormLabel>
          <Select
            value={documentPurpose}
            onChange={(e) => setFormData((prev) => ({ ...prev, purpose: e.target.value }))}
          >
            {DOCUMENT_PURPOSE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {showAssignee ? (
        <FormControl>
          <FormLabel>Assignee</FormLabel>
          <Input
            value={assigneeSearch}
            onChange={(e) => void searchAssignees(e.target.value)}
            placeholder="Search by name (min 2 chars)"
          />
          {assigneeLoading ? <Spinner size="sm" mt={2} /> : null}
          {assigneeResults.length > 0 ? (
            <Box mt={2} borderWidth="1px" borderRadius="md" maxH="160px" overflowY="auto">
              {assigneeResults.map((agent) => {
                const label =
                  [agent.first_name, agent.last_name].filter(Boolean).join(' ') ||
                  agent.user_login ||
                  `#${agent.id}`
                return (
                  <Button
                    key={agent.id}
                    variant="ghost"
                    size="sm"
                    w="100%"
                    justifyContent="flex-start"
                    onClick={() => {
                      setAssignedTo(agent.id)
                      setAssigneeSearch(label)
                      setAssigneeResults([])
                    }}
                  >
                    {label}
                  </Button>
                )
              })}
            </Box>
          ) : null}
        </FormControl>
      ) : null}
      {isDocCollectionCreate ? (
        <Text fontSize="sm" color="gray.600">
          Saves via the <strong>document_collection</strong> template so default assignee, priority, and due-date rules
          still apply. Upload attachments after the task is created.
        </Text>
      ) : null}
    </VStack>
  )

  const renderEditHeader = () => (
    <VStack align="stretch" spacing={3} mt={2}>
      {activeTask ? <TaskTypeBadge type={resolveTaskUiType(activeTask)} /> : null}
      <Wrap spacing={3} align="center">
        <WrapItem>
          <TaskPriorityBadge priority={priority} />
        </WrapItem>
        <WrapItem>
          <TaskStatusPill
            status={status}
            statusLabel={activeTask?.status_label}
            statusRecord={
              statusRecord
                ? {
                    code: statusRecord.value,
                    label: statusRecord.label,
                    is_terminal: statusRecord.is_terminal,
                    color: statusRecord.color,
                  }
                : null
            }
          />
        </WrapItem>
      </Wrap>
      {!isOnsiteCoordinationUi ? <HStack align="flex-end" spacing={3} flexWrap="wrap">
        {!moduleRendersBaseFields ? (
          <>
            <FormControl flex="1" minW="140px">
              <FormLabel fontSize="xs">Priority</FormLabel>
              <Select size="sm" value={priority} onChange={(e) => setPriority(e.target.value)}>
                {PRIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl flex="1" minW="180px">
              <FormLabel fontSize="xs">Due date</FormLabel>
              <Input
                size="sm"
                type="datetime-local"
                value={dueAt}
                onChange={(e) => setDueAt(e.target.value)}
              />
            </FormControl>
          </>
        ) : null}
        {activeTask && resolveTaskUiType(activeTask) === 'fe_visit' ? null : (
          <FormControl flex="1" minW="160px">
            <FormLabel fontSize="xs">Status</FormLabel>
            <Select size="sm" value={status} onChange={(e) => setStatus(e.target.value)}>
              {filteredStatusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </FormControl>
        )}
      </HStack> : null}
      {showAssignee && !isOnsiteCoordinationUi ? (
        <TaskAssigneePicker
          assignedTo={assignedTo}
          assigneeName={assigneeSearch}
          onChange={(userId, displayName) => {
            setAssignedTo(userId)
            setAssigneeSearch(displayName)
          }}
        />
      ) : null}
      {isEdit && activeTask?.id && !isOnsiteCoordinationUi ? (
        <TaskWatchersControl
          taskId={activeTask.id}
          watchers={watchers}
          onChange={setWatchers}
          isDisabled={taskReadOnly}
        />
      ) : null}
    </VStack>
  )

  const renderDetailsTab = () => {
    if (isOnsiteCoordinationUi && activeTask) {
      const metrics = activeTask.coordination_metrics
      const linkedIds = Array.isArray(activeTask.form_data?.linked_fe_visit_task_ids)
        ? activeTask.form_data.linked_fe_visit_task_ids.map(Number).filter(Number.isFinite)
        : metrics?.linked_fe_visit_task_ids ?? []
      const linkedVisits = siblingTasks.filter((sibling) => linkedIds.includes(sibling.id))
      const holdReason = activeTask.form_data?.hold_reason
      const holdRemarks = activeTask.form_data?.hold_remarks
      const expectedFollowUp = activeTask.form_data?.expected_follow_up

      return (
        <VStack align="stretch" spacing={4}>
          <Alert status={taskComplete ? 'success' : 'info'} borderRadius="md" alignItems="flex-start">
            <AlertIcon mt={0.5} />
            <Box>
              <Text fontSize="sm" fontWeight="700">
                {taskComplete ? 'Onsite coordination completed' : 'Onsite coordination in progress'}
              </Text>
              <Text fontSize="xs">
                {taskComplete
                  ? 'The dispatch prerequisites were completed and the linked FE Visit is now the active fieldwork task.'
                  : 'This system-managed task tracks the time spent preparing the onsite dispatch.'}
              </Text>
            </Box>
          </Alert>

          <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50">
            <VStack align="stretch" spacing={3}>
              <Box>
                <Text fontSize="xs" color="gray.500" fontWeight="700">STATUS</Text>
                <TaskStatusPill status={activeTask.status} statusLabel={activeTask.status_label} />
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" fontWeight="700">COORDINATOR</Text>
                <Text fontSize="sm">{activeTask.assignee_name ?? 'Unassigned'}</Text>
              </Box>
              {holdReason ? (
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="700">HOLD REASON</Text>
                  <Text fontSize="sm">{String(holdReason)}</Text>
                </Box>
              ) : null}
              {holdRemarks ? (
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="700">HOLD REMARKS</Text>
                  <Text fontSize="sm">{String(holdRemarks)}</Text>
                </Box>
              ) : null}
              {expectedFollowUp ? (
                <Box>
                  <Text fontSize="xs" color="gray.500" fontWeight="700">EXPECTED FOLLOW-UP</Text>
                  <Text fontSize="sm">{String(expectedFollowUp)}</Text>
                </Box>
              ) : null}
            </VStack>
          </Box>

          {metrics ? (
            <HStack align="stretch" spacing={3} flexWrap="wrap">
              {[
                ['Raw time', metrics.raw_minutes],
                ['Paused time', metrics.paused_minutes],
                ['Adjusted time', metrics.adjusted_minutes],
              ].map(([label, minutes]) => (
                <Box key={String(label)} borderWidth="1px" borderRadius="md" p={3} minW="130px" flex="1">
                  <Text fontSize="xs" color="gray.500" fontWeight="700">{label}</Text>
                  <Text fontSize="lg" fontWeight="700">{Number(minutes)} min</Text>
                </Box>
              ))}
            </HStack>
          ) : null}

          {linkedVisits.length > 0 ? (
            <Box>
              <Text fontSize="sm" fontWeight="700" mb={2}>Linked FE Visit</Text>
              <VStack align="stretch" spacing={2}>
                {linkedVisits.map((visit) => (
                  <Button
                    key={visit.id}
                    size="sm"
                    variant="outline"
                    justifyContent="space-between"
                    onClick={onOpenTask ? () => onOpenTask(visit) : undefined}
                    isDisabled={!onOpenTask}
                  >
                    <Text noOfLines={1}>{visit.title || visit.task_number}</Text>
                    <TaskStatusPill status={visit.status} statusLabel={visit.status_label} />
                  </Button>
                ))}
              </VStack>
            </Box>
          ) : null}
        </VStack>
      )
    }

    return (
    <VStack align="stretch" spacing={4}>
      {activeTask?.sla_due_at ? (
        <Box p={3} borderRadius="md" border="1px solid" borderColor="gray.200" bg="gray.50">
          <Text fontSize="xs" fontWeight="700" color="gray.600" mb={2}>
            SLA
          </Text>
          <SlaStatusStrip slaDueAt={activeTask.sla_due_at} />
        </Box>
      ) : null}

      <FormControl isRequired>
        <FormLabel>Title</FormLabel>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task description" />
      </FormControl>

      {activeTask ? (
        <TaskFormRenderer
          task={activeTask}
          parentTicketId={parentTicketId}
          formData={formData}
          onFormDataChange={(key, value) => setFormData((prev) => ({ ...prev, [key]: value }))}
          ticketPropertiesProps={mergedTicketPropertiesProps}
          onRefresh={onSaved}
        />
      ) : null}
    </VStack>
    )
  }

  return (
    <>
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size={isEdit ? 'lg' : 'md'}>
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          <Text fontSize="xs" color="gray.500" fontWeight="600">
            {activeTask?.task_number ?? 'New task'}
          </Text>
          <Text fontSize="md" fontWeight="700" noOfLines={2}>
            {isEdit ? title || 'Edit task' : 'Create task'}
          </Text>
          {isEdit ? renderEditHeader() : null}
        </DrawerHeader>
        <DrawerBody>
          {loadingTask ? (
            <HStack py={8} justify="center">
              <Spinner size="sm" />
              <Text fontSize="sm" color="gray.500">
                Loading task…
              </Text>
            </HStack>
          ) : isEdit && activeTask ? (
            <Tabs index={tabIndex} onChange={setTabIndex} variant="line" colorScheme="blue" isLazy>
              {isOnsiteCoordinationUi ? (
                <>
                  <TabList mb={4}>
                    <Tab fontSize="sm">Coordination</Tab>
                    <Tab fontSize="sm">Activity</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>{renderDetailsTab()}</TabPanel>
                    <TabPanel px={0}>
                      <TaskTimeline taskId={activeTask.id} isDisabled />
                    </TabPanel>
                  </TabPanels>
                </>
              ) : isFeVisitUi ? (
                <>
                  {/*
                    FE visit tabs: Visit timeline first (authoritative mobile lifecycle events).
                    Omit generic Checklist — TaskChecklistItem is a separate table from
                    FeChecklistTemplate/Item/Response that the mobile app uses via
                    FeVisitLifecycleService::assertRequiredChecklistComplete(); showing both
                    lets them disagree (finding 2.3). Unifying those systems is out of scope;
                    we only stop surfacing the wrong checklist as authoritative here.
                    Omit Attachments: showAttachmentsTab is true for fe_visit today
                    (!taskHostsDocumentCollection), but FE visits don't host attachments as a
                    distinct workflow surface — Activity/timeline covers relevant history.
                    Visit timeline is always shown here (not gated on showFeVisitTab); that
                    flag still suppresses the legacy "Visit events" tab for non-fe_visit UI
                    when core.fe_visit_tracking already hosts the timeline in Details.
                  */}
                  <TabList mb={4}>
                    <Tab fontSize="sm">Visit timeline</Tab>
                    <Tab fontSize="sm">More details</Tab>
                    <Tab fontSize="sm">Dependencies</Tab>
                    {showExpenseTab ? <Tab fontSize="sm">Expenses</Tab> : null}
                    <Tab fontSize="sm">Activity</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>
                      <FeVisitTimeline taskId={activeTask.id} />
                    </TabPanel>
                    <TabPanel px={0}>{renderDetailsTab()}</TabPanel>
                    <TabPanel px={0}>
                      <TaskDependencies
                        task={activeTask}
                        siblingTasks={siblingTasks}
                        isDisabled={taskComplete || taskSkipped || taskSuperseded}
                        onOpenTask={onOpenTask}
                        onChanged={onSaved}
                      />
                    </TabPanel>
                    {showExpenseTab ? (
                      <TabPanel px={0}>
                        <FeExpenseReviewPanel taskId={activeTask.id} isReadOnly={taskReadOnly} />
                      </TabPanel>
                    ) : null}
                    <TabPanel px={0}>
                      <TaskTimeline taskId={activeTask.id} isDisabled={taskReadOnly} />
                    </TabPanel>
                  </TabPanels>
                </>
              ) : isPartsProcurementUi ? (
                <>
                  <TabList mb={4}>
                    <Tab fontSize="sm">Parts</Tab>
                    <Tab fontSize="sm">More details</Tab>
                    <Tab fontSize="sm">
                      <HStack spacing={2}>
                        <Text as="span">Checklist</Text>
                        {checklistBadgeLabel ? (
                          <Badge
                            colorScheme={checklistIncomplete ? 'orange' : 'blue'}
                            variant="subtle"
                            fontSize="0.65rem"
                            borderRadius="full"
                            px={1.5}
                          >
                            {checklistBadgeLabel}
                          </Badge>
                        ) : null}
                      </HStack>
                    </Tab>
                    <Tab fontSize="sm">Dependencies</Tab>
                    <Tab fontSize="sm">Activity</Tab>
                    {showAttachmentsTab ? <Tab fontSize="sm">Attachments</Tab> : null}
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>
                      <PartProcurementPanel
                        standalone
                        ticketId={parentTicketId}
                        childTicketId={activeTask.id}
                        ticketParts={procurementParts}
                        isReadOnly={taskComplete || taskSkipped || taskSuperseded}
                        onSaved={() => {
                          onSaved()
                          void refreshProcurementParts()
                        }}
                      />
                    </TabPanel>
                    <TabPanel px={0}>{renderDetailsTab()}</TabPanel>
                    <TabPanel px={0}>
                      <TaskChecklist
                        taskId={activeTask.id}
                        isDisabled={taskComplete || taskSkipped || taskSuperseded}
                        onProgressChange={handleChecklistProgressChange}
                      />
                    </TabPanel>
                    <TabPanel px={0}>
                      <TaskDependencies
                        task={activeTask}
                        siblingTasks={siblingTasks}
                        isDisabled={taskComplete || taskSkipped || taskSuperseded}
                        onOpenTask={onOpenTask}
                        onChanged={onSaved}
                      />
                    </TabPanel>
                    <TabPanel px={0}>
                      <TaskTimeline taskId={activeTask.id} isDisabled={taskReadOnly} />
                    </TabPanel>
                    {showAttachmentsTab ? (
                      <TabPanel px={0}>
                        <TaskAttachmentsList taskId={activeTask.id} isDisabled={taskReadOnly} />
                      </TabPanel>
                    ) : null}
                  </TabPanels>
                </>
              ) : (
                <>
                  <TabList mb={4}>
                    <Tab fontSize="sm">Details</Tab>
                    <Tab fontSize="sm">
                      <HStack spacing={2}>
                        <Text as="span">Checklist</Text>
                        {checklistBadgeLabel ? (
                          <Badge
                            colorScheme={checklistIncomplete ? 'orange' : 'blue'}
                            variant="subtle"
                            fontSize="0.65rem"
                            borderRadius="full"
                            px={1.5}
                          >
                            {checklistBadgeLabel}
                          </Badge>
                        ) : null}
                      </HStack>
                    </Tab>
                    <Tab fontSize="sm">Dependencies</Tab>
                    {showExpenseTab ? <Tab fontSize="sm">Expenses</Tab> : null}
                    {showFeVisitTab ? <Tab fontSize="sm">Visit events</Tab> : null}
                    <Tab fontSize="sm">Activity</Tab>
                    {showAttachmentsTab ? <Tab fontSize="sm">Attachments</Tab> : null}
                  </TabList>
                  <TabPanels>
                    <TabPanel px={0}>{renderDetailsTab()}</TabPanel>
                    <TabPanel px={0}>
                      <TaskChecklist
                        taskId={activeTask.id}
                        isDisabled={taskComplete || taskSkipped || taskSuperseded}
                        onProgressChange={handleChecklistProgressChange}
                      />
                    </TabPanel>
                    <TabPanel px={0}>
                      <TaskDependencies
                        task={activeTask}
                        siblingTasks={siblingTasks}
                        isDisabled={taskComplete || taskSkipped || taskSuperseded}
                        onOpenTask={onOpenTask}
                        onChanged={onSaved}
                      />
                    </TabPanel>
                    {showExpenseTab ? (
                      <TabPanel px={0}>
                        <FeExpenseReviewPanel taskId={activeTask.id} isReadOnly={taskReadOnly} />
                      </TabPanel>
                    ) : null}
                    {showFeVisitTab ? (
                      <TabPanel px={0}>
                        <FeVisitTimeline taskId={activeTask.id} />
                      </TabPanel>
                    ) : null}
                    <TabPanel px={0}>
                      <TaskTimeline taskId={activeTask.id} isDisabled={taskReadOnly} />
                    </TabPanel>
                    {showAttachmentsTab ? (
                      <TabPanel px={0}>
                        <TaskAttachmentsList taskId={activeTask.id} isDisabled={taskReadOnly} />
                      </TabPanel>
                    ) : null}
                  </TabPanels>
                </>
              )}
            </Tabs>
          ) : (
            renderCreateBody()
          )}
        </DrawerBody>
        {isEdit && completionPreflight && !completionPreflight.can_complete ? (
          <Box px={6} pb={3}>
            <Alert status="warning" borderRadius="md" alignItems="flex-start">
              <AlertIcon mt={1} />
              <Box>
                <Text fontWeight="semibold">Completion blocked</Text>
                {completionPreflight.blockers.map((blocker) => (
                  <Text key={blocker.type} fontSize="sm">
                    {blocker.message}{blocker.items.length > 0 ? ` (${blocker.items.length})` : ''}
                  </Text>
                ))}
              </Box>
            </Alert>
          </Box>
        ) : null}
        {showOrphanFeVisitWarning ? (
          <Box px={6} pb={3}>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              This visit has no active schedule and cannot be reassigned.
            </Alert>
          </Box>
        ) : null}
        {taskSuperseded && activeTask ? (
          <Box px={6} pb={3}>
            <Alert status="info" borderRadius="md" alignItems="flex-start">
              <AlertIcon mt={1} />
              <Box>
                <Text fontWeight="semibold">Reassigned visit</Text>
                <Text fontSize="sm">
                  This visit was reassigned
                  {replacementTask?.assignee_name
                    ? ` to ${replacementTask.assignee_name}`
                    : ''}
                  {activeTask.status_changed_at
                    ? ` on ${new Date(activeTask.status_changed_at).toLocaleString()}`
                    : ''}
                  .
                </Text>
                <Text fontSize="sm" mt={1}>
                  Replacement task:{' '}
                  {replacementTask?.task_number ?? (activeTask.reassigned_to_task_id ? `#${activeTask.reassigned_to_task_id}` : '—')}.
                </Text>
                {typeof activeTask.form_data?.reassignment_reason === 'string' &&
                activeTask.form_data.reassignment_reason.trim() !== '' ? (
                  <Text fontSize="sm" mt={1}>
                    Reason: {activeTask.form_data.reassignment_reason.trim()}
                  </Text>
                ) : null}
                {replacementTask && onOpenTask ? (
                  <Button
                    size="xs"
                    mt={2}
                    variant="outline"
                    onClick={() => onOpenTask(replacementTask)}
                  >
                    Open replacement visit
                  </Button>
                ) : null}
              </Box>
            </Alert>
          </Box>
        ) : null}
        <DrawerFooter borderTopWidth="1px" flexWrap="wrap" gap={2}>
          {isEdit && isOnsiteCoordinationUi ? null : isEdit && isFeVisitUi ? (
            <>
              {canReassignFeVisit ? (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<UserRoundCog size={14} />}
                  onClick={() => setReassignModalOpen(true)}
                >
                  Reassign engineer
                </Button>
              ) : null}
              {showCancelVisit ? (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  leftIcon={<Ban size={14} />}
                  onClick={() => setCancelVisitDialogOpen(true)}
                  isLoading={actionLoading === 'cancel-visit'}
                >
                  Cancel visit
                </Button>
              ) : null}
              {showForceCloseVisit ? (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="orange"
                  leftIcon={<CheckCircle size={14} />}
                  onClick={() => setForceCloseDialogOpen(true)}
                  isLoading={actionLoading === 'force-close'}
                >
                  Force close
                </Button>
              ) : null}
            </>
          ) : (
            <>
              {canStart ? (
                <Button
                  size="sm"
                  variant="outline"
                  leftIcon={<Play size={14} />}
                  onClick={() => void handleStart()}
                  isLoading={actionLoading === 'start'}
                >
                  Start task
                </Button>
              ) : null}
              {isEdit && !taskComplete && !taskSkipped ? (
                <Button
                  size="sm"
                  colorScheme="green"
                  variant="outline"
                  leftIcon={<Check size={14} />}
                  onClick={() => void handleComplete()}
                  isLoading={actionLoading === 'complete'}
                  isDisabled={blockCompleteForParts}
                >
                  {blockCompleteForParts
                    ? `Mark complete (${unresolvedProcurementCount} not received)`
                    : 'Mark complete'}
                </Button>
              ) : null}
              {isEdit && !taskComplete && !taskSkipped ? (
                <Popover isOpen={skipPopoverOpen} onClose={() => setSkipPopoverOpen(false)} placement="top">
                  <PopoverTrigger>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="gray"
                      leftIcon={<SkipForward size={14} />}
                      onClick={() => setSkipPopoverOpen(true)}
                      isLoading={actionLoading === 'skip'}
                    >
                      Skip — not applicable
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent w={skipRequiresReasonForParts ? '320px' : '280px'}>
                    <PopoverArrow />
                    <PopoverCloseButton />
                    <PopoverBody pt={6} pb={4}>
                      <VStack align="stretch" spacing={3}>
                        <Text fontSize="sm" color="gray.600">
                          {skipRequiresReasonForParts
                            ? `${linkedProcurementParts.length} part${linkedProcurementParts.length === 1 ? ' is' : 's are'} linked to this task. Skipping will leave them without an active procurement task. Are you sure?`
                            : 'Mark this step as not applicable. The workflow will continue to the next step.'}
                        </Text>
                        <FormControl isRequired={skipRequiresReasonForParts}>
                          <FormLabel fontSize="xs">
                            {skipRequiresReasonForParts ? 'Reason (required)' : 'Reason (optional)'}
                          </FormLabel>
                          <Input
                            size="sm"
                            value={skipReason}
                            onChange={(e) => setSkipReason(e.target.value)}
                            placeholder={
                              skipRequiresReasonForParts
                                ? 'e.g. Procurement no longer needed'
                                : 'e.g. No parts needed'
                            }
                          />
                        </FormControl>
                        <Button
                          size="sm"
                          colorScheme="gray"
                          onClick={() => void handleSkip()}
                          isLoading={actionLoading === 'skip'}
                          isDisabled={skipRequiresReasonForParts && !skipReason.trim()}
                        >
                          Confirm skip
                        </Button>
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              ) : null}
              {isEdit && activeTask && !taskComplete && !taskSkipped && activeTask.status !== 'cancelled' ? (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="red"
                  leftIcon={<Ban size={14} />}
                  onClick={() => setCancelDialogOpen(true)}
                  isLoading={actionLoading === 'cancel'}
                >
                  Cancel task
                </Button>
              ) : null}
              {isEdit && activeTask && (taskComplete || taskSkipped) && !taskSuperseded ? (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="teal"
                  leftIcon={<RotateCcw size={14} />}
                  onClick={() => setReopenDialogOpen(true)}
                  isLoading={actionLoading === 'reopen'}
                >
                  Reopen
                </Button>
              ) : null}
            </>
          )}
          <Button size="sm" variant="outline" leftIcon={<X size={14} />} onClick={onClose}>
            Close
          </Button>
          {!taskSuperseded && !isOnsiteCoordinationUi ? (
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={isEdit ? <Save size={14} /> : <Plus size={14} />}
              onClick={() => void handleSave()}
              isLoading={saving}
              isDisabled={loadingDefaults}
            >
              {isEdit ? 'Save changes' : 'Create task'}
            </Button>
          ) : null}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    {/* Cancel task dialog */}
    <AlertDialog
      isOpen={cancelDialogOpen}
      leastDestructiveRef={cancelLeastDestructiveRef}
      onClose={() => setCancelDialogOpen(false)}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Cancel task
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={3} fontSize="sm" color="gray.600">
              This task will be marked as cancelled. Please provide a reason.
            </Text>
            <FormControl>
              <FormLabel fontSize="xs">Reason (required)</FormLabel>
              <Textarea
                size="sm"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Explain why this task is being cancelled…"
              />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelLeastDestructiveRef} onClick={() => setCancelDialogOpen(false)}>
              Back
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={() => void handleCancel()}
              isLoading={actionLoading === 'cancel'}
              isDisabled={!cancelReason.trim()}
            >
              Confirm cancel
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>

    {/* Reopen task dialog */}
    <AlertDialog
      isOpen={reopenDialogOpen}
      leastDestructiveRef={reopenLeastDestructiveRef}
      onClose={() => setReopenDialogOpen(false)}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Reopen task
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={3} fontSize="sm" color="gray.600">
              The task will be set back to <strong>pending</strong>. Optionally leave a note.
            </Text>
            <FormControl>
              <FormLabel fontSize="xs">Reason (optional)</FormLabel>
              <Textarea
                size="sm"
                rows={3}
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g. Needs to be reworked…"
              />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={reopenLeastDestructiveRef} onClick={() => setReopenDialogOpen(false)}>
              Back
            </Button>
            <Button
              colorScheme="teal"
              ml={3}
              onClick={() => void handleReopen()}
              isLoading={actionLoading === 'reopen'}
            >
              Confirm reopen
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>

    {/* Cancel field visit dialog */}
    <AlertDialog
      isOpen={cancelVisitDialogOpen}
      leastDestructiveRef={cancelVisitLeastDestructiveRef}
      onClose={() => setCancelVisitDialogOpen(false)}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Cancel visit
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={3} fontSize="sm" color="gray.600">
              Cancel this field visit?{' '}
              <strong>{activeTask?.assignee_name || 'The assigned engineer'}</strong> will no longer
              see this job in their app.
            </Text>
            <FormControl>
              <FormLabel fontSize="xs">Reason (optional)</FormLabel>
              <Textarea
                size="sm"
                rows={3}
                value={cancelVisitReason}
                onChange={(e) => setCancelVisitReason(e.target.value)}
                placeholder="Optional note for the cancellation…"
              />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelVisitLeastDestructiveRef} onClick={() => setCancelVisitDialogOpen(false)}>
              Back
            </Button>
            <Button
              colorScheme="red"
              ml={3}
              onClick={() => void handleCancelVisit()}
              isLoading={actionLoading === 'cancel-visit'}
            >
              Confirm cancel visit
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>

    {/* Force close field visit dialog */}
    <AlertDialog
      isOpen={forceCloseDialogOpen}
      leastDestructiveRef={forceCloseLeastDestructiveRef}
      onClose={() => setForceCloseDialogOpen(false)}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Force close visit
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={3} fontSize="sm" color="gray.600">
              Force-close this field visit? This marks the visit closed without waiting for the
              normal mobile close flow.
            </Text>
            <FormControl>
              <FormLabel fontSize="xs">Reason (optional)</FormLabel>
              <Textarea
                size="sm"
                rows={3}
                value={forceCloseReason}
                onChange={(e) => setForceCloseReason(e.target.value)}
                placeholder="Optional note for the force close…"
              />
            </FormControl>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={forceCloseLeastDestructiveRef} onClick={() => setForceCloseDialogOpen(false)}>
              Back
            </Button>
            <Button
              colorScheme="orange"
              ml={3}
              onClick={() => void handleForceCloseVisit()}
              isLoading={actionLoading === 'force-close'}
            >
              Confirm force close
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>

    {activeTask ? (
      <ReassignEngineerModal
        isOpen={reassignModalOpen}
        onClose={() => setReassignModalOpen(false)}
        parentTicketId={parentTicketId}
        task={activeTask}
        sourceSchedule={linkedFeSchedule}
        onReassigned={({ new_task: newTask }) => {
          onSaved()
          if (onOpenTask) {
            onOpenTask(newTask)
          }
        }}
      />
    ) : null}
  </>
  )
}
